import * as React from 'react';

import { Button, LoadingPanel, PageSplitDescription, PageSplitTitle } from '@gitbook/spine-react';
import { useHeadTitle } from '@gitbook/util-react-head';
import { useLoadingState } from '@gitbook/util-react-hooks';

import { MFAScreenModal } from '../../Components';
import { useSetAnalyticsScreen } from '../../navigation';
import { type FirebaseActionURLParams, useAuthOperations } from '../../state';
import { LoginScreenLayout } from './LoginScreenLayout';

/**
 * Step to verify the email address after changing it.
 */
export function StepApplyVerifyAndChangeEmailLink(props: {
    action: FirebaseActionURLParams;
}): React.ReactElement {
    const { action } = props;
    const { mfa, applyActionCode, checkActionCode } = useAuthOperations();
    const [loading, wrapLoading] = useLoadingState(true);

    useHeadTitle('Email changed');
    useSetAnalyticsScreen('account_settings_verify_email_change');

    React.useEffect(() => {
        wrapLoading(async () => {
            const info = await checkActionCode(action.oobCode);
            if (info) {
                await applyActionCode(action.oobCode);
            }
        });
    }, [action.oobCode, applyActionCode, checkActionCode, wrapLoading]);

    if (mfa) {
        return <MFAScreenModal mfa={mfa} />;
    }

    if (loading) {
        return <LoadingPanel size="large" />;
    }

    return (
        <LoginScreenLayout>
            <PageSplitTitle>Email changed</PageSplitTitle>
            <PageSplitDescription>
                Your email has been changed. You can now sign in with your new email address.
            </PageSplitDescription>
            <Button
                label="Go to login"
                size="large"
                kind="primary"
                className="mb-4"
                onPress={() => {
                    window.location.replace('/');
                }}
            />
        </LoginScreenLayout>
    );
}
